import React from "react";

export default function PrivacyPolicyPage() {
  return (
    <main className="min-h-screen bg-gradient-to-b from-white to-gray-50 text-gray-800">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-indigo-600 text-white shadow-sm">
              <span className="text-lg font-bold">G</span>
            </div>
            <div>
              <h1 className="text-xl font-semibold leading-tight">Gefmart</h1>
              <p className="text-xs text-gray-500">Privacy Policy</p>
            </div>
          </div>
          <nav className="hidden gap-6 text-sm sm:flex">
            <a href="#introduction" className="hover:text-indigo-600">
              Introduction
            </a>
            <a href="#company-information" className="hover:text-indigo-600">
              Company Info
            </a>
            <a href="#information-we-collect" className="hover:text-indigo-600">
              Data We Collect
            </a>
            <a href="#how-we-use" className="hover:text-indigo-600">
              How We Use
            </a>
            <a href="#sharing" className="hover:text-indigo-600">
              Sharing
            </a>
            <a href="#security" className="hover:text-indigo-600">
              Security
            </a>
            <a href="#retention" className="hover:text-indigo-600">
              Retention
            </a>
            <a href="#your-rights" className="hover:text-indigo-600">
              Your Rights
            </a>
            <a href="#contact" className="hover:text-indigo-600">
              Contact
            </a>
          </nav>
        </div>
      </header>

      {/* Hero / Title Block */}
      <section className="border-b bg-white">
        <div className="mx-auto max-w-6xl px-6 py-10">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Privacy Policy
          </h2>
          <p className="mt-3 max-w-6xl text-sm text-gray-600">
            This Privacy Policy explains how{" "}
            <span className="font-medium">Gefmart</span>, owned and operated by
            Geften Electrical Solutions LLP, collects, uses, discloses, and
            protects your information when you use the Gefmart app.
          </p>
        </div>
      </section>

      <div className="mx-auto grid max-w-6xl gap-6 px-6 py-10 md:grid-cols-[260px_1fr]">
        {/* Sidebar / TOC */}
        <aside className="order-2 md:order-1">
          <div className="sticky top-[84px] rounded-2xl border bg-white p-4 shadow-sm">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
              On this page
            </p>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  className="group inline-flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50"
                  href="#introduction"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-300 group-hover:bg-indigo-500" />
                  Introduction
                </a>
              </li>
              <li>
                <a
                  className="group inline-flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50"
                  href="#company-information"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-300 group-hover:bg-indigo-500" />
                  Company Information
                </a>
              </li>
              <li>
                <a
                  className="group inline-flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50"
                  href="#information-we-collect"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-300 group-hover:bg-indigo-500" />
                  Information We Collect
                </a>
              </li>
              <li>
                <a
                  className="group inline-flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50"
                  href="#how-we-use"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-300 group-hover:bg-indigo-500" />
                  How We Use Your Information
                </a>
              </li>
              <li>
                <a
                  className="group inline-flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50"
                  href="#sharing"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-300 group-hover:bg-indigo-500" />
                  How We Share
                </a>
              </li>
              <li>
                <a
                  className="group inline-flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50"
                  href="#security"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-300 group-hover:bg-indigo-500" />
                  Security
                </a>
              </li>
              <li>
                <a
                  className="group inline-flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50"
                  href="#retention"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-300 group-hover:bg-indigo-500" />
                  Data Retention
                </a>
              </li>
              <li>
                <a
                  className="group inline-flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50"
                  href="#your-rights"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-300 group-hover:bg-indigo-500" />
                  Your Rights
                </a>
              </li>
              <li>
                <a
                  className="group inline-flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50"
                  href="#contact"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-300 group-hover:bg-indigo-500" />
                  Contact
                </a>
              </li>
            </ul>
          </div>
        </aside>

        {/* Main content */}
        <article className="order-1 space-y-8 md:order-2">
          {/* Introduction */}
          <section
            id="introduction"
            className="rounded-2xl border bg-white p-6 shadow-sm"
          >
            <h3 className="text-xl font-semibold">Introduction</h3>
            <p className="mt-3 leading-relaxed text-gray-700">
              Gefmart is an app owned and operated by Geften Electrical
              Solutions LLP. At Gefmart, we are committed to protecting the
              privacy of our users. This Privacy Policy outlines how we collect,
              use, disclose, and protect your personal information when you use
              our <span className="italic">Gefmart</span> app.
            </p>
          </section>

          {/* Company Information */}
          <section
            id="company-information"
            className="rounded-2xl border bg-white p-6 shadow-sm"
          >
            <h3 className="text-xl font-semibold">Company Information</h3>
            <div className="mt-4 grid gap-6 md:grid-cols-2">
              <div>
                <h4 className="text-sm font-medium text-gray-500">
                  Registered Address
                </h4>
                <address className="mt-2 not-italic leading-relaxed">
                  Geften Electrical Solutions LLP
                  <br />
                  Kalathingal Building, 13/722F Poly Quarters Road,
                  <br />
                  Angadippuram PO, Perinthalmanna, Malappuram (Dist),
                  <br />
                  Kerala - 679321
                </address>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">
                  Branch Address
                </h4>
                <address className="mt-2 not-italic leading-relaxed">
                  Geften Electrical Solutions LLP
                  <br />
                  39/2475-B1, Suite 716, LR Towers, SJRRA 104,
                  <br />
                  South Janatha Road, Palarivattom, Kochi,
                  <br />
                  Kerala - 682025
                </address>
              </div>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              <p>
                <span className="font-medium">GST Number:</span> 32ABAFG3398L1Z7
              </p>
              <p>
                <span className="font-medium">Contact Number:</span>{" "}
                <a
                  href="tel:+918921951010"
                  className="text-indigo-600 hover:underline"
                >
                  +91 892 195 1010
                </a>
              </p>
              <p>
                <span className="font-medium">Contact Email:</span>{" "}
                <a
                  href="mailto:gefmartsupport@geften.com"
                  className="text-indigo-600 hover:underline"
                >
                  gefmartsupport@geften.com
                </a>
              </p>
            </div>
          </section>

          {/* Information We Collect */}
          <section
            id="information-we-collect"
            className="rounded-2xl border bg-white p-6 shadow-sm"
          >
            <h3 className="text-xl font-semibold">Information We Collect</h3>
            <ul className="mt-4 list-inside space-y-3">
              <li className="rounded-lg bg-gray-50 p-4">
                <span className="font-medium">
                  Account Creation Information:
                </span>{" "}
                When you create an account, we may collect your name, email
                address, phone number, and username.
              </li>
              <li className="rounded-lg bg-gray-50 p-4">
                <span className="font-medium">Location Data:</span> If you
                enable location services, we may collect your device's location
                data to provide location-based features.
              </li>
              <li className="rounded-lg bg-gray-50 p-4">
                <span className="font-medium">Usage Data:</span> Information
                about how you interact with our app, such as pages visited,
                features used, and time spent in the app.
              </li>
              <li className="rounded-lg bg-gray-50 p-4">
                <span className="font-medium">Device Information:</span> Details
                such as device model, operating system, and IP address.
              </li>
              <li className="rounded-lg bg-gray-50 p-4">
                <span className="font-medium">Order Information:</span> Details
                necessary to process your orders, including shipping address,
                billing information, and items purchased.
              </li>
              <li className="rounded-lg bg-gray-50 p-4">
                <span className="font-medium">Payment Information:</span> We do
                not directly collect or store credit card details. Payment
                transactions are handled securely by a third-party payment
                processor.
              </li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section
            id="how-we-use"
            className="rounded-2xl border bg-white p-6 shadow-sm"
          >
            <h3 className="text-xl font-semibold">
              How We Use Your Information
            </h3>
            <ul className="mt-4 list-disc space-y-2 pl-6">
              <li>
                <span className="font-medium">
                  To provide and improve our services:
                </span>{" "}
                operate the app, process orders, personalize your experience,
                and improve features.
              </li>
              <li>
                <span className="font-medium">To communicate with you:</span>{" "}
                send order confirmations, updates, promotional offers (only if
                you opt in), and important account-related notices.
              </li>
              <li>
                <span className="font-medium">Customer support:</span> provide
                assistance and address your inquiries.
              </li>
              <li>
                <span className="font-medium">Analytics:</span> use anonymized
                data to better understand how users interact with our app and
                make improvements.
              </li>
            </ul>
          </section>

          {/* Sharing */}
          <section
            id="sharing"
            className="rounded-2xl border bg-white p-6 shadow-sm"
          >
            <h3 className="text-xl font-semibold">
              How We Share Your Information
            </h3>
            <ul className="mt-4 list-disc space-y-2 pl-6">
              <li>
                <span className="font-medium">
                  Third-party service providers:
                </span>{" "}
                we may share your information with service providers who help us
                operate our services, such as shipping companies, payment
                processors, and analytics providers.
              </li>
              <li>
                <span className="font-medium">Legal requirements:</span> we may
                disclose your information if required by law, such as to comply
                with a subpoena or court order.
              </li>
              <li>
                <span className="font-medium">Business transfers:</span> in the
                event of a merger, acquisition, or sale of our company, we may
                transfer your information to the new entity.
              </li>
            </ul>
          </section>

          {/* Security */}
          <section
            id="security"
            className="rounded-2xl border bg-white p-6 shadow-sm"
          >
            <h3 className="text-xl font-semibold">
              Security of Your Information
            </h3>
            <p className="mt-3 leading-relaxed text-gray-700">
              We take appropriate security measures to protect your information
              from unauthorized access, use, disclosure, or alteration. This
              includes, but is not limited to, encryption, firewalls, and access
              controls.
            </p>
          </section>

          {/* Data Retention */}
          <section
            id="retention"
            className="rounded-2xl border bg-white p-6 shadow-sm"
          >
            <h3 className="text-xl font-semibold">Data Retention</h3>
            <p className="mt-3 leading-relaxed text-gray-700">
              We retain your information for as long as necessary to fulfill the
              purposes outlined in this Privacy Policy or as required by law.
            </p>
          </section>

          {/* Your Rights */}
          <section
            id="your-rights"
            className="rounded-2xl border bg-white p-6 shadow-sm"
          >
            <h3 className="text-xl font-semibold">Your Rights</h3>
            <p className="mt-3 leading-relaxed text-gray-700">
              Depending on your jurisdiction, you may have the following rights:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>
                <span className="font-medium">Right to access:</span> request a
                copy of the personal information we hold about you.
              </li>
              <li>
                <span className="font-medium">Right to rectification:</span>{" "}
                request that we correct inaccurate information about you.
              </li>
              <li>
                <span className="font-medium">Right to erasure:</span> request
                that we delete your personal information.
              </li>
              <li>
                <span className="font-medium">
                  Right to restrict processing:
                </span>{" "}
                request that we limit processing of your personal information.
              </li>
              <li>
                <span className="font-medium">Right to data portability:</span>{" "}
                receive your personal information in a structured and portable
                format.
              </li>
            </ul>
            <p className="mt-3 text-gray-700">
              To exercise these rights, please contact us at{" "}
              <a
                href="mailto:gefmartsupport@geften.com"
                className="font-medium text-indigo-600 hover:underline"
              >
                gefmartsupport@geften.com
              </a>
              .
            </p>
          </section>

          {/* Contact */}
          <section
            id="contact"
            className="rounded-2xl border bg-white p-6 shadow-sm"
          >
            <h3 className="text-xl font-semibold">Contact</h3>
            <p className="mt-3 leading-relaxed text-gray-700">
              If you have any questions about this Privacy Policy or our data
              practices, please contact us:
            </p>
            <div className="mt-4 grid gap-4 md:grid-cols-3 contactUsPrivacy">
              <div className="rounded-xl border bg-gray-50 p-4">
                <p className="text-sm font-medium text-gray-500">Phone</p>
                <a
                  href="tel:+918921951010"
                  className="mt-1 block text-base font-semibold hover:underline"
                >
                  +91 892 195 1010
                </a>
              </div>
              <div className="rounded-xl border bg-gray-50 p-4">
                <p className="text-sm font-medium text-gray-500">Email</p>
                <a
                  href="mailto:gefmartsupport@geften.com"
                  className="mt-1 block text-base font-semibold hover:underline"
                >
                  gefmartsupport@geften.com
                </a>
              </div>
              <div className="rounded-xl border bg-gray-50 p-4">
                <p className="text-sm font-medium text-gray-500">Website</p>
                <a
                  href="#"
                  className="mt-1 block text-base font-semibold hover:underline"
                >
                  gefmart.com
                </a>
              </div>
            </div>
          </section>

          {/* Footer note */}
          {/* <section className="rounded-2xl border bg-white p-6 text-sm text-gray-500 shadow-sm">
            <p>
              <span className="font-medium text-gray-700">Last updated:</span>{" "}
              {new Date().toLocaleDateString()}
            </p>
          </section> */}
        </article>
      </div>

      <footer className="border-t bg-white/70">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-6 text-sm text-gray-500">
          <p>
            © {new Date().getFullYear()} Geften Electrical Solutions LLP. All
            rights reserved.
          </p>
          <a
            href="#introduction"
            className="rounded-full border px-3 py-1 hover:bg-gray-50"
          >
            Back to top
          </a>
        </div>
      </footer>
    </main>
  );
}
